'use client';

import { twJoin } from 'tailwind-merge';
import { useState, useRef } from 'react';
import { Heading } from '@zopauk/web-ui';
import { Band } from '@/components/Modules/Band/Band';
import { StoryboardSlider } from './StoryboardSlider';
import type { StoryboardFragment } from '@/utils/dato/generated';
import { useTabListKeyNav } from '@/utils/hooks/useTabListKeyNav';

const Storyboard = ({ subtitle, title, tabs }: StoryboardFragment) => {
  const allTabs = tabs ?? [];
  const [activeTabId, setActiveTabId] = useState<string>(allTabs[0]?.id ?? '');
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const handleTabKeyDown = useTabListKeyNav(allTabs, activeTabId, setActiveTabId, tabRefs);

  if (!allTabs.length) {
    return null;
  }

  const activeTab = allTabs.find((tab) => tab.id === activeTabId) ?? allTabs[0];

  return (
    <Band data-automation="ZA.storyboard-band">
      <div className="max-lg:max-w-[812px] max-lg:mx-auto">
        <div className="text-center mb-8">
          {title ? (
            <Heading className="mb-5" size="h2" as="h2">
              {title}
            </Heading>
          ) : null}

          {subtitle ? <p>{subtitle}</p> : null}
        </div>

        {allTabs.length > 1 ? (
          <div
            className="flex justify-center gap-3 mb-7 flex-wrap"
            onKeyDown={handleTabKeyDown}
            aria-label="Storyboard tabs"
            role="tablist"
          >
            {allTabs.map((tab, index) => {
              const isActive = tab.id === activeTab.id;
              const tabButtonId = `storyboard-tab-${tab.id}`;

              return (
                <button
                  className={twJoin(
                    'px-5 py-3 rounded-full font-semibold transition-colors cursor-pointer text-sm lg:text-base',
                    isActive ? 'bg-[#387360] text-[#fff]' : 'bg-transparent text-primary-content hover:bg-[#387360]/30',
                  )}
                  ref={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  onClick={() => setActiveTabId(tab.id)}
                  aria-controls="storyboard-panel"
                  tabIndex={isActive ? 0 : -1}
                  aria-selected={isActive}
                  id={tabButtonId}
                  type="button"
                  key={tab.id}
                  role="tab"
                >
                  {tab.tabLabel}
                </button>
              );
            })}
          </div>
        ) : null}

        <div aria-labelledby={`storyboard-tab-${activeTab.id}`} id="storyboard-panel" role="tabpanel" tabIndex={0}>
          <StoryboardSlider items={activeTab.items ?? []} />
        </div>
      </div>
    </Band>
  );
};

export default Storyboard;
